import { useEffect, useState } from "react";
import type { Rpc } from "../lib/rpc";

type Tab = "catalog" | "queued" | "described" | "captioned";
const TABS: Array<{ key: Tab; label: string; icon: string }> = [
  { key: "catalog",   label: "Catalog",   icon: "📁" },
  { key: "queued",    label: "Queued",    icon: "📋" },
  { key: "described", label: "Described", icon: "📝" },
  { key: "captioned", label: "Captioned", icon: "✅" },
];

interface ContentFile {
  name: string;
  path: string;
  size: number;
  mtime: string;
  isVideo: boolean;
}

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function fmtMtime(iso: string): string {
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export function ContentPage({ rpc, electronConnected }: { rpc: Rpc; electronConnected: boolean }) {
  const [tab, setTab] = useState<Tab>("catalog");
  const [files, setFiles] = useState<ContentFile[]>([]);
  const [folder, setFolder] = useState<string>("");
  const [filter, setFilter] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!electronConnected) return;
    let cancelled = false;
    setLoading(true);
    setErr(null);
    rpc.call<{ ok: boolean; files?: ContentFile[]; folder?: string; error?: string }>("get-content-files", tab)
      .then((r) => {
        if (cancelled) return;
        if (!r.ok) { setErr(r.error || "Failed to load"); setFiles([]); return; }
        setFiles(r.files || []);
        setFolder(r.folder || "");
      })
      .catch((e) => { if (!cancelled) setErr((e as Error).message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [rpc, electronConnected, tab]);

  const visible = files.filter((f) =>
    !filter || f.name.toLowerCase().includes(filter.toLowerCase())
  );

  if (!electronConnected) {
    return (
      <section className="settings-page">
        <header className="settings-page-header"><h2>🎬 Content Browser</h2></header>
        <div className="empty">Electron must be running on the worker PC.</div>
      </section>
    );
  }

  return (
    <section className="settings-page">
      <header className="settings-page-header">
        <h2>🎬 Content Browser</h2>
        <div className="header-actions">
          {err && <span className="err">{err}</span>}
          <input
            type="text" placeholder="filter…"
            value={filter} onChange={(e) => setFilter(e.target.value)}
            className="content-filter"
          />
        </div>
      </header>

      <div className="content-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            className={`content-tab ${tab === t.key ? "active" : ""}`}
            onClick={() => setTab(t.key)}
          >
            <span>{t.icon}</span> {t.label}
            {tab === t.key && <span className="content-tab-count">{visible.length}</span>}
          </button>
        ))}
      </div>

      <div className="content-info-bar">
        <span className="muted">
          {loading ? "loading…" : `${visible.length} of ${files.length} files`}
        </span>
        <span className="content-folder">{folder}</span>
      </div>

      <div className="content-table">
        <div className="content-row content-row-header">
          <span>Name</span>
          <span>Type</span>
          <span>Size</span>
          <span>Modified</span>
        </div>
        {visible.slice(0, 500).map((f) => (
          <div key={f.path} className="content-row" title={f.path}>
            <span className="content-name">{f.name}</span>
            <span className="muted">{f.isVideo ? "video" : "image"}</span>
            <span className="muted">{fmtBytes(f.size)}</span>
            <span className="muted">{fmtMtime(f.mtime)}</span>
          </div>
        ))}
        {visible.length === 0 && !loading && (
          <div className="empty">No files matching filter.</div>
        )}
        {visible.length > 500 && (
          <div className="muted small" style={{ padding: 10 }}>
            (only showing first 500 of {visible.length} matching)
          </div>
        )}
      </div>
    </section>
  );
}
